// Sets up app specific configurations
import '~/config/components/artwork';
import type { Intent } from '@jet/environment/dispatching';
import type { ActionModel } from '@jet/environment/types/models';
import { initializeUniqueIdContext } from '@amp/web-app-components/src/utils/uniqueId';
import {
    DEFAULT_STOREFRONT_CODE,
    DEFAULT_LANGUAGE_BCP47,
} from '~/constants/storefront';
import { Jet } from '~/jet';
import type { Page } from '~/jet/models';
import { Locale } from '~/utils/locale';
import { setup as setupI18n } from '~/stores/i18n';
import type { PrefetchedIntents } from '@amp/web-apps-common/src/jet/prefetched-intents';
import type { LoggerFactory } from '@amp/web-apps-logger';
import type { Locale as Language } from '@amp/web-apps-localization';
import type I18N from '@amp/web-apps-localization';
import { createDefaultRoutingObject } from '@amp/web-apps-common/src/utils/createDefaultRoutingObject';
import type { UserCallbacks } from '~/jet/dependencies/net';

export type Context = Map<string, unknown>;

export async function bootstrap(
    loggerFactory: LoggerFactory,
    initialUrl: string,
    fetch: typeof window.fetch,
    userCallbacks: UserCallbacks,
    prefetchedIntents: PrefetchedIntents,
    defaultStorefront?: string,
): Promise<{
    context: Context;
    jet: Jet;
    initialAction: ActionModel | null;
    intent: Intent<Page>;
    storefront: string;
    language: string;
    locale: Locale;
    i18n: I18N;
}> {
    const log = loggerFactory.loggerFor('bootstrap');

    const context = new Map();

    const origin = new URL(initialUrl).origin;
    const jet = Jet.load(
        loggerFactory,
        context,
        fetch,
        userCallbacks,
        origin,
        prefetchedIntents,
        defaultStorefront,
    );

    initializeUniqueIdContext(context, loggerFactory);

    const routing = await jet.routeUrl(initialUrl);

    if (routing) {
        log.info('initial URL routed to:', routing);
    } else {
        log.warn('initial URL was unroutable:', initialUrl);
    }

    const defaultRoutingObj = createDefaultRoutingObject(initialUrl);

    const {
        intent = null,
        action: initialAction = null,
        storefront = DEFAULT_STOREFRONT_CODE,
        language = DEFAULT_LANGUAGE_BCP47,
    } = routing || {
        ...defaultRoutingObj,
        ...Locale.normalize(defaultRoutingObj),
    };

    const locale = Locale.load(context, storefront, language);

    // TODO: rdar://78109398 (i18n Improvements)
    const i18nStore = await setupI18n(
        context,
        loggerFactory,
        language.toLowerCase() as Language,
    );
    jet.setLocale(i18nStore, storefront, language);

    return {
        context,
        jet,
        initialAction,
        intent,
        storefront,
        language,
        locale,
        i18n: i18nStore,
    };
}
